import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import type { PalantirProgressBarMode } from './progress-bar.type';

@Component({
  selector: 'palantir-progress-bar',
  templateUrl: './progress-bar.component.html',
  styleUrls: ['./progress-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [],
})
export class PalantirProgressComponent implements OnChanges {
  @Input() public progress: number = 0;

  @Input() public total: number = 100;

  @Input() public mode: PalantirProgressBarMode = 'determinate';

  @Output() public progressChange = new EventEmitter<number>();

  public _progress = signal<number>(this.progress);

  public _mode = signal<PalantirProgressBarMode>(this.mode);

  private dragging = false;

  public constructor(private readonly host: ElementRef<HTMLElement>) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['progress'] || changes['total'] || changes['mode']) {
      this._progress.set(this.calculateProgress());
      this._mode.set(this.mode);
    }
  }

  private calculateProgress(): number {
    if (this.mode === 'indeterminate') {
      return 0;
    }

    if (this.progress === null) {
      return 100;
    }

    if (this.progress > this.total) {
      return 100;
    }

    if (this.progress < 0) {
      return 0;
    }

    return (this.progress / this.total) * 100;
  }

  public onDragStart(event: PointerEvent): void {
    if (this.mode !== 'draggable') {
      return;
    }

    event.preventDefault();
    this.dragging = true;

    window.addEventListener('pointermove', this.onDrag);
    window.addEventListener('pointerup', this.onDragEnd);

    this.onDrag(event);
  }

  private readonly onDrag = (event: PointerEvent): void => {
    if (!this.dragging) {
      return;
    }

    const rect = this.host.nativeElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;

    const width = rect.width;
    const progress = Math.max(0, Math.min(100, (offsetX / width) * 100));

    this._progress.set(progress);
    this.progressChange.emit(progress);
  };

  private readonly onDragEnd = (): void => {
    this.dragging = false;

    window.removeEventListener('pointermove', this.onDrag);
    window.removeEventListener('pointerup', this.onDragEnd);
  };
}
